import tabDetails from '../components/Navigation/tabDetails';
import Oidc from './oidc';
import Session from './session';
import { stripTrailingSlash } from './utils';

const authenticate = async (oidcUserManager, homeURL, errorLogger) => {
  try {
    await oidcUserManager.clearStaleState();
    try {
      // promise to trigger a redirect of the current window to the authorization endpoint.
      const redirectUri = homeURL ? stripTrailingSlash(homeURL) : undefined;
      await oidcUserManager.signinRedirect(redirectUri ? { redirect_uri: redirectUri } : {});
    } catch (err) {
      throw new errorLogger({}, 'There was an issue with signinRedirect during authentication', err);
    }
  } catch (err) {
    throw new errorLogger({}, 'There was an issue clearing stale state during authentication', err);
  }
};

const login = async (oidcUserManager, hash, adminURL, homeURL, errorLogger) => {
  let authState = '';

  let parts = hash.substring(1).split('&');
  for (let i = 0; i < parts.length; i++) {
    var nv = parts[i].split('=');
    if (nv[0] == 'state') {
      authState = nv[1];
    }
  }

  try {
    // promise to process response from the authorization endpoint. The result of the promise is the authenticated User.
    const user = await oidcUserManager.signinRedirectCallback();
    if (user) {
      // Log User in and redirect Home `/`
      Session.set(user.id_token, user.profile, authState, user.access_token);
      /**
       * location: {
       *   href: 'https://reports.mapnwea.org/map?authorization_correlation_id=123',
       *   search: '?authorization_correlation_id=123'
       * }
       * remove the #search content from url -> 'https://reports.mapnwea.org/map'
       */
      location.href = homeURL || location.href.substring(0, location.href.indexOf(location.search));
    } else {
      // User logged in (via Central Auth) but doesn't have required permissions to view Application
      location.href = adminURL;
    }
  } catch (err) {
    location.href = adminURL;
    throw new errorLogger({}, 'There was an issue during login', err);
  }
};

const setupSilentRenew = async (oidcUserManager, updateFN, authState, context) => {
  // Set Silent Renew Event Handler (fires when `access_token` about to expire)
  oidcUserManager.events.addAccessTokenExpiring(async () => {
    // Start Silent Renew of token (updating the Session Cookie with new `access_token` from Admin / Central Auth)
    try {
      // promise to trigger a silent request (via an iframe) to the authorization endpoint. The result of the promise is the authenticated User.
      const user = await oidcUserManager.signinSilent({ state: authState });
      if (typeof updateFN === 'function') {
        // This mechanism is used to update the Bearer token when silent renew kicks off
        updateFN(user.access_token);
      } else if (updateFN) {
        //an updateFN was supplied - but its not a function - console warn about this.
        console.warn('Authentication manager was supplied an unusable update function.');
      }
    } catch (err) {
      await context.logout();
      throw new context.logError({}, err);
    }
  });
};

export class AuthManager {
  constructor(config) {
    this.config = config;
    // Setting bind here to allow clearSession & logout to be called via eventHandler in correct context
    this.init = this.init.bind(this);
    this.clearSession = this.clearSession.bind(this);
    this.logout = this.logout.bind(this);

    Oidc.configureUserManager(config);

    this.logError = config.LOG_ERROR
      ? config.LOG_ERROR
      : class DefaultErrorLogger extends Error {
          constructor(props, ...params) {
            super(...params);
            console.error(this);
          }
        };
  }

  async init() {
    let session = Session.get();
    if (session.authState && session.user) {
      //=============================================
      // HOP 3 - save session info and destroy cookie
      //=============================================
      // We retrieve the stored session information from the session Cookie, then destroy it.
      // If the user refreshes the browser we will contact the oidc server (using the oidc library) to re-authenticate,
      // it manages the user session through the oidc id_token stored within session storage
      this.session = session;
      this.userManager = Oidc.UserManager;
      Session.destroy();
      return setupSilentRenew(Oidc.UserManager, this.config.UPDATE_FN, session.authState, this);
    } else if (window.location.href.includes('authorization_correlation_id')) {
      //=============================================
      // HOP 2 - Retrieve user info
      //=============================================
      // Login authenticates with the oidc server and writes Session information to a cookie before redirecting to HOME_URL
      // On redirect to the home URL, the session information stored in this class instance will be lost, hence the need to save it to a cookie
      return login(
        Oidc.UserManager,
        window.location.search,
        this.config.START_PAGE_URL,
        this.config.HOME_URL,
        this.logError
      );
    } else {
      //=============================================
      // HOP 1 - AUTHENTICATE
      //=============================================
      // We have no session, and no hash# from the oidc server, authenticate with oidc to start the 3 hop process off
      // This will send the user to the login page, then redirect back to the main SPA index page which will reload this init
      // function, but passing a hash containing the users details from the oidc server
      const redirectURL = location.href;
      return authenticate(Oidc.UserManager, redirectURL, this.logError);
    }
  }

  clearSession() {
    // Terminate Session
    Session.destroy();
  }
  /**
   * No longer getting called - logout url provided by userMenu endpoint
   */
  async logout() {
    this.clearSession();

    // Logout User & Destroy Session cookie
    if (this.session && this.session.authState) {
      try {
        await this.userManager.signoutRedirect({ state: this.session.authState });
      } catch (err) {
        throw new this.logError({}, err);
      }
    }
  }
}
